import { Controller } from 'stimulus'
import formatCurrency from 'helpers/new-format-currency'
import Axios from 'axios'

/**
 * Manages js for the borrower assets step (StimulusJS controller)
 *
 * @class BorrowerAssetsController
 * @memberof Controllers
 */
export default class extends Controller {
  static targets = [
    'accountAmount',
    'accountAmountError',
    'accountName',
    'accountNameError',
    'accountOwner',
    'accountOwnerError',
    'accountSource',
    'accountSourceError',
    'assetClearOrCancelButton',
    'assetCreateOrUpdateButton',
    'assetTotal',
    'assetsList',
    'control',
    'noAssetError',
    'templateSource',
    'accountchekSSN',
    'modalContent',
    'modalMainContent',
    'accountchekInitButton',
    'accountchekRunButton',
    'dialogContent'
  ]

  axios = Axios.create({
    headers: { 'Accept': 'application/json' }
  })

  /**
   * @function Controllers.BorrowerAssetsController.accountchekRun
   * @memberof Controllers
   * @description Makes the backend call to retrieve the accountchek widget
   *
   * @param {Event} event - User initiated event
   */
  accountchekRun(event) {
    const ssn      = this.accountchekSSNTarget.value
    const formType = this.data.get('application')
    const slug     = this.data.get('slug')

    if (this._shortSsnValid()) {
      event.preventDefault()
      this.accountchekInitButtonTarget.disabled = true
      this.accountchekRunButtonTarget.disabled  = true
      this.dialogContentTarget.scrollTo({ top: 0, behavior: 'smooth' })

      this.axios.post('/v1/accountchek_assets', { ssn, form_type: formType, slug })
        .then(function(response) {
          this._handleAccountchekSuccess(response)
        }.bind(this))
        .catch(function(error) {
          // console.error('Accountchek assets widget error: ', error)
          this._handleAccountchekRunFail()
        }.bind(this))
    }
  }

  /**
   * @function Controllers.BorrowerAssetsController.createOrUpdate
   * @memberof Controllers
   * @description Creates or updates an asset, or throws errors on asset on
   * client-side click event.
   */
  createOrUpdate() {
    const { inputLegit, ...fieldsLegit } = this._inputLegit()
    const HTTPVerb = this.assetCreateOrUpdateButtonTarget.dataset.method

    if (inputLegit) {
      this._postOrUpdate(HTTPVerb)
    } else {
      this._removeErrorState()
      this._displayFieldErrors(fieldsLegit)
    }
  }

  /**
   * @function Controllers.BorrowerAssetsController.editAsset
   * @memberof Controllers
   * @description Removes an asset from the list and places its values in the
   * form for update.
   *
   * @param {Event} event - User initiated click event.
   */
  editAsset(event) {
    const assetElement   = event.target.parentElement.parentElement.parentElement.parentElement
    const controlElement = assetElement.querySelector('.crud-item-list__list-item-controls')

    const amount = controlElement.dataset.amount
    const id     = controlElement.dataset.id
    const name   = assetElement.querySelector('.crud-item-list__list-item-name').innerText
    const source = assetElement.querySelector('.crud-item-list__list-item-account').innerText
    const owner  = assetElement.querySelector('.crud-item-list__list-item-owner').innerText

    this.accountSourceTarget.value = source
    this.accountNameTarget.value   = name
    this.accountAmountTarget.value = amount
    this.accountAmountTarget.dispatchEvent(new CustomEvent('change'))

    if (this.data.get('coborrower') === 'true') {
      this.accountOwnerTarget.value = owner
    }

    this.cachedAsset = {
      amount,
      id,
      name,
      owner,
      source
    }

    assetElement.remove()

    this._calculateTotal()
    this._changeButtonsToEdit(id)
  }

  /**
   * @function Controllers.BorrowerAssetsController.clearOrCancel
   * @memberof Controllers
   * @description Upon UI button click, either clears the form fields or kicks
   * off an update call to the back-end.
   */
  clearOrCancel() {
    const method = this.assetClearOrCancelButtonTarget.dataset.method

    if (method === 'CLEAR') {
      this._blankValues()
      this._removeErrorState()
    } else {
      this._addAssetToUI(this.cachedAsset)
      this.cachedAsset = null
      this._calculateTotal()
      this._blankValues()
      this._removeErrorState()
    }

    this._changeButtonsToCreate()
  }

  /**
   * @function Controllers.BorrowerAssetsController.destroyAsset
   * @memberof Controllers
   * @description Destroys an asset on click from the user.
   * @param {Event} event - User initiated click event.
   */
  destroyAsset(event) {
    event.preventDefault()

    const assetId = event.target.parentElement.parentElement.dataset.id

    fetch(`/v1/borrower_assets/${assetId}`, {
      method:      'DELETE',
      credentials: 'same-origin',
      headers:     { 'content-type': 'application/json' },
      mode:        'cors'
    })
      .then(response => this._removeAssetFromUI(event.target))
      .catch(error => {
      // console.error('Error: ', error)
      })
  }

  // Private

  /**
   * @function Controllers.BorrowerAssetsController._handleAccountchekRunFail
   * @memberof Controllers
   * @private
   *
   * @return {undefined} - manipulates DOM
   */
  _handleAccountchekRunFail() {
    const fragment = document.createRange().createContextualFragment(`
      <p>We're sorry. An unexpected error has occurred. Please close this window, and manually add your assets.</p>
    `)
    this.modalMainContentTarget.innerText = ''
    this.modalMainContentTarget.appendChild(fragment)
  }

  /**
   * @function Controllers.BorrowerAssetsController._shortSsnValid
   * @memberof Controllers
   * @description Triggers required-field controller to validate the ssn field.
   * @private
   *
   * @return {Boolean} validity - true if valid otherwise false
   */
  _shortSsnValid() {
    const validator = this.application.getControllerForElementAndIdentifier(this.element, 'required-field')
    return validator.validate()
  }

  /**
   * @function Controllers.BorrowerAssetsController._handleAccountChekSuccess
   * @memberof Controllers
   * @description Triggers a bankend call to get the accountchek widget. Sets
   * up listeners for both the event from a successful iframe session or the
   * closing of the modal by the user (the potential accountchek round and
   * round UI scenario)
   * @private
   *
   * @param {Object} response - The result of the backend call
   */
  _handleAccountchekSuccess(response) {
    if (response.data !== '') {
      this.modalContentTarget.classList.toggle('account-chek__modal--active')

      this.modalContentTarget.innerHTML = ''
      const fragment = document.createRange().createContextualFragment(response.data)
      this.modalContentTarget.appendChild(fragment)

      window.addEventListener('message', function(event) {
        window.dispatchEvent(new CustomEvent('modal:hide'))
      })

      window.addEventListener('modal:hide', function(event) {
        this._handleModalClose()
      }.bind(this))
    } else {
      this._handleAccountchekRunFail()
    }
  }

  /**
   * @function Controllers.BorrowerAssetsController._handleModalClose
   * @memberof Controllers
   * @description Handles checking in with accountchek to see what assets were
   * created and places them in the UI upon modal close. This runs both when
   * accountchek fires its own 'message' event and when the user manually
   * closes the modal.
   * @private
   */
  _handleModalClose() {
    const formType = this.data.get('application')
    const slug     = this.data.get('slug')

    this.axios.get(`/v1/accountchek_assets?form_type=${formType}&slug=${slug}`)
      .then(function(response) {
        this._handleReportSuccess(response)
      }.bind(this))
      .catch(function(error) {
        // console.error('Accountchek report error: ', error)
      })
  }

  /**
   * @function Controllers.BorrowerAssetsController._handleReportSuccess
   * @memberof Controllers
   * @description If assets were present on the backend accountchek report
   * call, they're added to the UI
   * @private
   *
   * @param {Object} response - Result of Axios call
   */
  _handleReportSuccess(response) {
    if (response.data !== '') {
      response.data.forEach(asset => {
        this._addAssetToUI(asset)
      })
    }
    this._calculateTotal()
  }

  /**
   * @function Controllers.BorrowerAssetsController._postOrUpdate
   * @memberof Controllers
   * @description Posts or updates to the backend
   * @private
   *
   * @param {String} HTTPVerb - Verb to use for call to back-end
   */
  _postOrUpdate(HTTPVerb) {
    if (HTTPVerb == 'POST') {
      this._createAsset()
    } else {
      const id = this.assetCreateOrUpdateButtonTarget.dataset.id

      this._updateAsset(id)
      this._changeButtonsToCreate()
    }
  }

  /**
   * @function Controllers.BorrowerAssetsController._changeButtonsToEdit
   * @memberof Controllers
   * @description Manipulates the text of the form buttons to match the edit
   * state, 'cancel' and 'save', and alters dataset state to be used by other
   * methods.
   * @private
   *
   * @param {String} id - asset id
   */
  _changeButtonsToEdit(id) {
    this.assetClearOrCancelButtonTarget.innerText      = 'Cancel'
    this.assetClearOrCancelButtonTarget.dataset.method = 'CANCEL'

    this.assetCreateOrUpdateButtonTarget.innerText      = 'Save'
    this.assetCreateOrUpdateButtonTarget.dataset.method = 'PATCH'
    this.assetCreateOrUpdateButtonTarget.dataset.id     = id
  }

  /**
   * @function Controllers.BorrowerAssetsController._changeButtonsToCreate
   * @memberof Controllers
   * @description Manipulates the text of the form buttons to match the edit
   * state, 'clear' and 'add asset', and alters dataset state to be used by
   * other methods.
   * @private
   */
  _changeButtonsToCreate() {
    this.assetClearOrCancelButtonTarget.innerText      = 'Clear'
    this.assetClearOrCancelButtonTarget.dataset.method = 'CLEAR'

    this.assetCreateOrUpdateButtonTarget.innerText      = 'Add Asset'
    this.assetCreateOrUpdateButtonTarget.dataset.method = 'POST'
    this.assetCreateOrUpdateButtonTarget.dataset.id     = ''
  }

  /**
   * @function Controllers.BorrowerAssetsController._displayFieldErrors
   * @memberof Controllers
   * @description Takes an object with booleans for each required field and
   * displays errors for those that aren't legit.
   * @private
   *
   * @param {Object} fieldsLegit
   * @param {Boolean} fieldsLegit.amountLegit - true if amount input legit, false otherwise
   * @param {Boolean} fieldsLegit.OwnerLegit - true if owner input legit, false otherwise
   * @param {Boolean} fieldsLegit.SourceLegit - true if source input legit, false otherwise
   * @param {Boolean} fieldsLegit.accountNameLegit - true if source input legit, false otherwise
   */
  _displayFieldErrors(fieldsLegit) {
    if (!fieldsLegit.amountLegit) {
      this.accountAmountTarget.classList.add('form__text-field--error')
      this.accountAmountErrorTarget.classList.remove('form__errors-list--hidden')
    }

    if (!fieldsLegit.ownerLegit) {
      this.accountOwnerTarget.classList.add('form__select--error')
      this.accountOwnerErrorTarget.classList.remove('form__errors-list--hidden')
    }

    if (!fieldsLegit.sourceLegit) {
      this.accountSourceTarget.classList.add('form__select--error')
      this.accountSourceErrorTarget.classList.remove('form__errors-list--hidden')
    }

    if (!fieldsLegit.accountNameLegit) {
      this.accountNameTarget.classList.add('form__text-field--error')
      this.accountNameErrorTarget.classList.remove('form__errors-list--hidden')
    }
  }

  /**
   * @function Controllers.BorrowerAssetsController._inputLegit
   * @memberof Controllers
   * @description Checks the required fields to see if they have a non-empty
   *
   * @private
   *
   * @return {Object} fieldsLegit
   * @return {Boolean} fieldsLegit.amountLegit - true if present, false otherwise
   * @return {Boolean} fieldsLegit.OwnerLegit - true if present, false otherwise
   * @return {Boolean} fieldsLegit.SourceLegit - true if present, false otherwise
   * @return {Boolean} fieldsLegit.accountNameLegit - true if present, false otherwise
   */
  _inputLegit() {
    const amountLegit      = this.accountAmountTarget.value !== ''
    const ownerLegit       = this.accountOwnerTarget.value !== ''
    const sourceLegit      = this.accountSourceTarget.value !== ''
    const accountNameLegit = this.accountNameTarget.value !== ''

    const inputLegit = amountLegit && ownerLegit && sourceLegit && accountNameLegit

    return {
      inputLegit,
      amountLegit,
      ownerLegit,
      sourceLegit,
      accountNameLegit
    }
  }

  /**
   * @function Controllers.BorrowerAssetsController._removeErrorState
   * @memberof Controllers
   * @description Removes all the errors from the UI.
   * @private
   */
  _removeErrorState() {
    this.noAssetErrorTarget.innerText = ''
    this.accountAmountTarget.classList.remove('form__text-field--error')
    this.accountAmountErrorTarget.classList.add('form__errors-list--hidden')

    this.accountNameTarget.classList.remove('form__text-field--error')
    this.accountNameErrorTarget.classList.add('form__errors-list--hidden')

    this.accountSourceTarget.classList.remove('form__select--error')
    this.accountSourceErrorTarget.classList.add('form__errors-list--hidden')

    if (this.data.get('coborrower') === 'true') {
      this.accountOwnerTarget.classList.remove('form__select--error')
      this.accountOwnerErrorTarget.classList.add('form__errors-list--hidden')
    }
  }

  /**
   * @function Controllers.BorrowerAssetsController._blankValues
   * @memberof Controllers
   * @description Removes all the values from the form fields.
   * @private
   */
  _blankValues() {
    this.accountAmountTarget.value = ''
    this.accountAmountTarget.imask.updateValue()
    this.accountNameTarget.value   = ''
    this.accountSourceTarget.value = ''

    if (this.data.get('coborrower') === 'true') {
      this.accountOwnerTarget.value  = ''
    }
  }

  /**
   * @function Controllers.BorrowerAssetsController._getFormValues
   * @memberof Controllers
   * @description Gets values from the form for other parties
   * @private
   *
   * @return {Object} form_values
   * @return {String} form_values.amount Asset amount
   * @return {String} form_values.name Asset name
   * @return {String} form_values.owner Asset owner
   * @return {String} form_values.source Asset source
   */
  _getFormValues() {
    const amount = this._removeCommas(this.accountAmountTarget.value)
    const name   = this.accountNameTarget.value
    const owner  = this.accountOwnerTarget.value
    const source = this.accountSourceTarget.value

    return { amount, name, owner, source }
  }

  /**
   * @function Controllers.BorrowerAssetsController._prepareAssetFromForm
   * @memberof Controllers
   * @description Gets form values from _getFormValues and adds an id to it
   * @private
   *
   * @param  {String} id
   * @return {Object} form_values
   * @return {String} form_values.amount Asset amount
   * @return {String} form_values.name Asset name
   * @return {String} form_values.owner Asset owner
   * @return {String} form_values.source Asset source
   * @return {String} form_values.id Asset id
   */
  _prepareAssetFromForm(id) {
    const { amount, name, owner, source } = this._getFormValues()

    return { amount, id, name, owner, source }
  }

  /**
   * @function Controllers.BorrowerAssetsController._successResolution
   * @memberof Controllers
   * @description Called on the successful resolution of a POST or UPDATE call
   * to the back-end, clears form, removes errors, adds the asset to the asset
   * list, and calculates the asset total
   * @private
   *
   * @param {Object} asset
   * @param {Integer} asset.id
   * @param {String} asset.name
   * @param {String} asset.owner
   * @param {String} asset.amount
   * @param {String} asset.source
   */
  _successResolution(asset) {
    this._blankValues()
    this._removeErrorState()
    this._addAssetToUI(asset)
    this._calculateTotal()
  }

  /**
   * @function Controllers.BorrowerAssetsController._calculateTotal
   * @memberof Controllers
   * @description Loops through the control targets pulling the amount,
   * accumulating it
   * @private
   */
  _calculateTotal() {
    const total = this.controlTargets.reduce((acc, target) => {
      const maybeAmount = target.dataset.amount

      if (maybeAmount) {
        return acc + parseInt(maybeAmount)
      }

      return acc
    }, 0)

    this.assetTotalTarget.textContent = formatCurrency(total)
  }

  /**
   * @function Controllers.BorrowerAssetsController._removeAssetFromUI
   * @memberof Controllers
   * @description Removes an asset from the ui and recalculates total
   *
   * @param {Object} element - Asset element to remove
   * @private
   */
  _removeAssetFromUI(element) {
    element.parentElement.parentElement.parentElement.remove()

    this._calculateTotal()
  }

  /**
   * @function Controllers.BorrowerAssetsController._addAssetToUI
   * @memberof Controllers
   * @description Adds an asset to the ui using the template.
   * @private
   *
   * @param {Object} asset
   * @param {Integer} asset.id
   * @param {String} asset.name
   * @param {String} asset.owner
   * @param {String} asset.amount
   * @param {String} asset.source
   * @param {String} asset.accountchek - optional if the asset is from accountchek
   */
  _addAssetToUI(asset) {
    const clone = this.templateSourceTarget.cloneNode(true)

    clone.removeAttribute('style')
    clone.removeAttribute('data-target')
    clone.querySelector('.crud-item-list__list-item-account').textContent = asset.source
    clone.querySelector('.crud-item-list__list-item-name').textContent    = asset.name

    if (this.data.get('coborrower') === 'true') {
      clone.querySelector('.crud-item-list__list-item-owner').textContent = asset.owner
    }

    clone.querySelector('.crud-item-list__list-item-total').textContent = formatCurrency(asset.amount)

    if (asset.accountchek) {
      clone.querySelector('.crud-item-list__list-item-controls-normal').remove()
      clone.querySelector('.crud-item-list__list-item-controls').dataset.amount = asset.amount
    } else {
      clone.querySelector('.crud-item-list__list-item-controls-accountchek-container').remove()
      clone.querySelector('.crud-item-list__list-item-controls').dataset.id     = asset.id
      clone.querySelector('.crud-item-list__list-item-controls').dataset.amount = asset.amount
    }

    const firstChild = this.assetsListTarget.firstElementChild

    this.assetsListTarget.insertBefore(clone, firstChild)
  }

  /**
   * @function Controllers.BorrowerAssetsController._removeCommas
   * @memberof Controllers
   * @description Removes the commas from the input masked number
   * @private
   *
   * @param {String} value - value with commas
   *
   * @return {String} Same value without commas
   *
   * @example
   *
   * input  = '300,000'
   * output = '300000'
   */
  _removeCommas(value) {
    return value.replace(/,/g, '')
  }

  /**
   * @function Controllers.BorrowerAssetsController._updateAsset
   * @memberof Controllers
   * @description Retrieves form values, combines it with the id and sends it
   * off to the back-end for persistence
   * @private
   *
   * @param {String} id - asset id
   */
  _updateAsset(id) {
    const asset = this._getFormValues()
    const data  = { asset, id }

    fetch(`/v1/borrower_assets/${id}`, {
      method:      'PATCH',
      credentials: 'same-origin',
      headers:     { 'content-type': 'application/json' },
      mode:        'cors',
      body:        JSON.stringify(data)
    })
      .then(response => response.json())
      .then(response => this._successResolution(response))
      .catch(error => {
        // console.error('Error: ', error)
      })
  }

  /**
   * @function Controllers.BorrowerAssetsController._createAsset
   * @memberof Controllers
   * @description Creates an asset through a call to the back-end
   * off to the back-end for persistence
   * @private
   */
  _createAsset() {
    const slug        = this.data.get('slug')
    const application = this.data.get('application')
    const asset       = this._getFormValues()

    const data = { asset, application, slug }

    fetch('/v1/borrower_assets', {
      method:      'POST',
      credentials: 'same-origin',
      headers:     { 'content-type': 'application/json' },
      mode:        'cors',
      body:        JSON.stringify(data)
    })
      .then(response => response.json())
      .then(response => this._successResolution(response))
      .catch(error => {
      // console.error('Error: ', error)
      })
  }
}
